import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, addDoc, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from '../firebase';
import { FiArrowLeft, FiSave, FiImage } from 'react-icons/fi';
import '../styles/BlogEditor.css';

const BlogEditor = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [imageUploading, setImageUploading] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        excerpt: '',
        content: '',
        author: '',
        category: '',
        imageUrl: '',
        status: 'published'
    });

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (!user) {
                navigate('/admin/login');
            }
        });

        if (id) {
            fetchBlog();
        }

        return () => unsubscribe();
    }, [id, navigate]);

    const fetchBlog = async () => {
        try {
            const blogDoc = await getDoc(doc(db, 'blogs', id));
            if (blogDoc.exists()) {
                setFormData(blogDoc.data());
            }
        } catch (error) {
            console.error('Error fetching blog:', error);
            alert('Error loading blog');
        }
    };

    const compressAndConvertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target.result;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;
                    const MAX_WIDTH = 1000;
                    const MAX_HEIGHT = 1000;

                    if (width > height) {
                        if (width > MAX_WIDTH) {
                            height = Math.round(height * (MAX_WIDTH / width));
                            width = MAX_WIDTH;
                        }
                    } else {
                        if (height > MAX_HEIGHT) {
                            width = Math.round(width * (MAX_HEIGHT / height));
                            height = MAX_HEIGHT;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);
                    // Instant compact Base64 storage (<0.1s processing)
                    const dataUrl = canvas.toDataURL('image/jpeg', 0.75);
                    resolve(dataUrl);
                };
                img.onerror = (error) => reject(error);
            };
            reader.onerror = (error) => reject(error);
        });
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            alert('Please upload an image file (कृपया इमेज फाईल सिलेक्ट करा)');
            return;
        }

        // Validate file size: max 2MB as requested by user
        if (file.size > 2 * 1024 * 1024) {
            alert('Image size should be 2MB or less (प्रतिमा आकार २MB पेक्षा कमी असावा)');
            e.target.value = '';
            return;
        }

        setImageUploading(true);
        try {
            // Instant Base64 image compression - zero network delay!
            const base64Url = await compressAndConvertToBase64(file);
            setFormData(prev => ({ ...prev, imageUrl: base64Url }));
        } catch (error) {
            console.error('Error processing image:', error);
            alert('Error processing image. Please try again.');
        } finally {
            setImageUploading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.title || !formData.content) {
            alert('Please fill in all required fields');
            return;
        }

        setLoading(true);
        try {
            const blogData = {
                ...formData,
                updatedAt: serverTimestamp()
            };

            if (id) {
                // Update existing blog
                await updateDoc(doc(db, 'blogs', id), blogData);
                alert('Blog updated successfully!');
            } else {
                // Create new blog
                blogData.createdAt = serverTimestamp();
                await addDoc(collection(db, 'blogs'), blogData);
                alert('Blog created successfully!');
            }

            navigate('/admin/dashboard');
        } catch (error) {
            console.error('Error saving blog:', error);
            alert('Error saving blog. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    return (
        <div className="blog-editor">
            <div className="editor-header">
                <div className="editor-header-content">
                    <button
                        className="btn-back"
                        onClick={() => navigate('/admin/dashboard')}
                    >
                        <FiArrowLeft /> Back to Dashboard
                    </button>
                    <h1>{id ? 'Edit Blog' : 'Create New Blog'}</h1>
                </div>
            </div>

            <div className="editor-content">
                <form onSubmit={handleSubmit} className="blog-form">
                    <div className="form-section">
                        <h2>Basic Information</h2>

                        <div className="form-group">
                            <label htmlFor="title">Title *</label>
                            <input
                                type="text"
                                id="title"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                placeholder="Enter blog title"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="excerpt">Excerpt</label>
                            <textarea
                                id="excerpt"
                                name="excerpt"
                                value={formData.excerpt}
                                onChange={handleChange}
                                placeholder="Brief description of the blog (optional)"
                                rows="3"
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="author">Author</label>
                                <input
                                    type="text"
                                    id="author"
                                    name="author"
                                    value={formData.author}
                                    onChange={handleChange}
                                    placeholder="Author name"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="category">Category</label>
                                <input
                                    type="text"
                                    id="category"
                                    name="category"
                                    value={formData.category}
                                    onChange={handleChange}
                                    placeholder="e.g., Health, Wellness"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h2>Featured Image</h2>
                        <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '15px' }}>
                            Upload image up to 2MB (saved in Firestore database).
                        </p>

                        <div className="image-upload-container">
                            {formData.imageUrl && (
                                <div className="image-preview" style={{ position: 'relative', display: 'inline-block' }}>
                                    <img src={formData.imageUrl} alt="Preview" />
                                    <button
                                        type="button"
                                        className="btn-remove-image"
                                        onClick={() => setFormData(prev => ({ ...prev, imageUrl: '' }))}
                                        style={{
                                            position: 'absolute',
                                            top: '10px',
                                            right: '10px',
                                            background: '#f44336',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '50%',
                                            width: '28px',
                                            height: '28px',
                                            cursor: 'pointer',
                                            fontWeight: 'bold',
                                            boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                                        }}
                                        title="Remove Image"
                                    >
                                        ✕
                                    </button>
                                </div>
                            )}

                            <div className="upload-controls">
                                <label htmlFor="image-upload" className="btn-upload">
                                    <FiImage /> {formData.imageUrl ? 'Change Image' : 'Upload Image (Max 2MB)'}
                                </label>
                                <input
                                    type="file"
                                    id="image-upload"
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    style={{ display: 'none' }}
                                />
                                {imageUploading && <span className="uploading-text">Processing & saving image...</span>}
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h2>Content *</h2>

                        <div className="form-group">
                            <textarea
                                id="content"
                                name="content"
                                value={formData.content}
                                onChange={handleChange}
                                placeholder="Write your blog content here..."
                                rows="15"
                                required
                            />
                        </div>
                    </div>

                    <div className="form-section">
                        <h2>Publishing Options</h2>

                        <div className="form-group">
                            <label htmlFor="status">Status</label>
                            <select
                                id="status"
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                            >
                                <option value="published">Published</option>
                                <option value="draft">Draft</option>
                            </select>
                        </div>
                    </div>

                    <div className="form-actions">
                        <button
                            type="button"
                            className="btn-cancel"
                            onClick={() => navigate('/admin/dashboard')}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn-save"
                            disabled={loading || imageUploading}
                        >
                            <FiSave /> {loading ? 'Saving...' : (id ? 'Update Blog' : 'Publish Blog')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default BlogEditor;
